import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertTriangle, Calendar, Download } from 'lucide-react';
import { getPredictions, getTrends } from '../store/slices/analyticsSlice';
import historicalAnalysis from '../services/historicalAnalysis';

const PredictiveAnalytics: React.FC = () => {
  const dispatch = useDispatch();
  const { predictions, trends, loading } = useSelector((state: any) => state.analytics);
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'year'>('week');

  useEffect(() => {
    // Load initial data
    dispatch(getTrends(timeframe) as any);
  }, [timeframe, dispatch]);

  const handleAnalyzeCurrent = () => {
    const currentData = {
      type: 'flood',
      zone: 'Zone A',
      weather: 'heavy rain'
    };
    dispatch(getPredictions(currentData) as any);
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="text-purple-500" />
        AI-POWERED ANALYTICS
      </h2>

      {/* Timeframe Selector */}
      <div className="flex gap-2 mb-6">
        {(['week', 'month', 'year'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTimeframe(t)}
            className={`px-3 py-1 rounded-lg text-sm ${
              timeframe === t ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400'
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Trend Analysis */}
      {trends && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-black/30 rounded-lg">
            <p className="text-xs text-gray-400">Total Incidents</p>
            <p className="text-3xl font-bold text-cyan-500">{trends.totalIncidents}</p>
          </div>
          <div className="p-4 bg-black/30 rounded-lg">
            <p className="text-xs text-gray-400">Average Casualties</p>
            <p className="text-3xl font-bold text-orange-500">
              {Math.round(trends.averageCasualties)}
            </p>
          </div>
        </div>
      )}

      {/* Predictions Button */}
      <button
        onClick={handleAnalyzeCurrent}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-4 rounded-lg mb-4"
      >
        ANALYZE CURRENT SITUATION
      </button>

      {/* Prediction Results */}
      {loading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto" />
        </div>
      )}

      {predictions.map((pred: any, i: number) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-500/10 rounded-lg border border-red-500/30 mb-3"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="font-bold">{pred.zone}</p>
              <p className="text-sm text-gray-400">
                {Math.round(pred.probability * 100)}% probability
              </p>
            </div>
            <span className="text-2xl font-bold text-red-500">
              {pred.estimatedCasualties}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-2">Predicted in: {pred.timeFrame}</p>
        </motion.div>
      ))}

      {/* Export Button */}
      <button className="w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center gap-2 mt-4">
        <Download size={16} />
        EXPORT HISTORICAL DATA
      </button>
    </div>
  );
};

export default PredictiveAnalytics;